import React from 'react';
import { Link } from 'react-router-dom';
import { Artwork } from '../../types/Artwork';
import { ViewingRoomMode } from '../../types/ViewingRoomMode';
import { MEDIUM_DISPLAY } from '../../types/Medium';
import { YEAR_DISPLAY } from '../../types/Year';
import { ArtworkImage } from '../common/ArtworkImage';
import { Breadcrumb } from '../common/Breadcrumb';

interface ViewingRoomProps {
    artwork: Artwork;
    /** Global light/dark presentation. */
    mode: ViewingRoomMode;
    sectionPath: string[];
    onPrev: () => void;
    onNext: () => void;
}

// The "viewing room": the artwork hung on a wall (drop shadow) with its
// metadata and prev/next. Light ("white room") is the default; dark is the same layout on
// the gallery-wall surface (#191919), edge-to-edge. AA contrast holds in both.
export const ViewingRoom = ({ artwork, mode, sectionPath, onPrev, onNext }: ViewingRoomProps) => {
    const dark = mode === ViewingRoomMode.Dark;

    // Dark mode bleeds over the AppLayout <main> padding (px-8 py-8 / md:px-10) to fill
    // the content area edge-to-edge, like a gallery wall.
    const surface = dark ? 'bg-room-dark -mx-8 -my-8 px-8 py-8 md:-mx-10 md:px-10' : '';
    const shadow = dark ? 'shadow-wall-dark' : 'shadow-wall';
    const titleColor = dark ? 'text-room-ink' : 'text-ink';
    const metaColor = dark ? 'text-tone-500' : 'text-tone-900';
    const button = dark ? 'btn-dark' : 'btn';

    return (
        <section className={`flex min-h-[70vh] flex-col ${surface}`}>
            <Breadcrumb path={sectionPath} trailing={artwork.title} variant={dark ? 'dark' : 'light'} />

            <div className="flex flex-1 items-center justify-center py-6">
                {/* Prev/next glitch fix: fit="fill" makes the image fill its (aspect-matched) box
                    edge-to-edge — no object-contain sub-pixel letterbox seam (the "white line"). The
                    shadow is a box-shadow (shadow-wall), not filter: drop-shadow, so it can't desync
                    from the box during the swap. key remounts a fresh <img> per piece; neighbours are
                    preloaded in ViewingRoomPage so the swap stays instant. */}
                <ArtworkImage
                    key={artwork.id}
                    artwork={artwork}
                    fit="fill"
                    loading="eager"
                    className={`max-h-[54vh] max-w-[80%] ${shadow}`}
                />
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="leading-relaxed">
                    <h1 className={`text-title ${titleColor}`}>{artwork.title}</h1>
                    <div className={`text-meta italic ${metaColor}`}>
                        {MEDIUM_DISPLAY[artwork.medium]} — {artwork.dimensions}
                    </div>
                    <div className={`text-meta italic ${metaColor}`}>{YEAR_DISPLAY[artwork.year]}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {/* Return to the current section's gallery, restoring the visitor's scroll position. */}
                    <Link to={`/${sectionPath.join('/')}`} state={{ restoreScroll: true }} className={button}>
                        back to gallery
                    </Link>
                    <button type="button" className={button} onClick={onPrev} aria-label="Previous work">
                        ‹ previous
                    </button>
                    <button type="button" className={button} onClick={onNext} aria-label="Next work">
                        next ›
                    </button>
                </div>
            </div>
        </section>
    );
};
